import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Smartphone, Leaf, Brain, Globe, Network } from 'lucide-react';
import AnimatedSection from '../AnimatedSection';

const TechSection: React.FC = () => {
  return (
    <div className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-avn-purple-100 px-4 py-2 rounded-full text-avn-purple-700 mb-8">
            <Zap className="h-5 w-5" />
            <span>AVN.AI Technology</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Smart Agriculture for a Better Future
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our integrated technology system combines AI, IoT sensors, and blockchain verification to create transparent, ethical agricultural supply chains.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-16">
            <AnimatedSection>
              <img 
                src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d" 
                alt="AVN.AI Agriculture Technology System" 
                className="rounded-xl shadow-xl w-full h-auto"
              />
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="space-y-8">
                <h3 className="text-2xl font-bold text-gray-900">Our Technology Ecosystem</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-avn-purple-100 rounded-lg mt-1">
                      <Brain className="h-5 w-5 text-avn-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">AI-Driven Analytics</h4>
                      <p className="text-gray-600">Predictive modeling, crop health monitoring, and market intelligence to optimize farming decisions.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-avn-purple-100 rounded-lg mt-1">
                      <Smartphone className="h-5 w-5 text-avn-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">Mobile Farm Management</h4>
                      <p className="text-gray-600">Monitor crops, track inputs, and manage farm operations from anywhere with our mobile platform.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-avn-purple-100 rounded-lg mt-1">
                      <Network className="h-5 w-5 text-avn-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">Blockchain Verification</h4>
                      <p className="text-gray-600">Immutable records of ethical practices, fair compensation, and supply chain transparency.</p>
                    </div>
                  </div>
                </div>
                
                <div className="pt-4">
                  <a 
                    href="/avn" 
                    className="inline-flex items-center gap-2 text-avn-purple-600 hover:text-avn-purple-700 font-medium"
                  >
                    <span>Learn more about our technology</span>
                    <Zap className="h-5 w-5" />
                  </a>
                </div>
              </div>
            </AnimatedSection>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <AnimatedSection delay={0.1}>
              <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                <div className="text-avn-purple-600 mb-6">
                  <Globe className="h-12 w-12" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Precision Agriculture</h3>
                <p className="text-gray-600 mb-6">
                  Smart drones and IoT sensors collect real-time data on crop health, soil conditions, and environmental factors to optimize farming practices.
                </p>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.2}>
              <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                <div className="text-avn-purple-600 mb-6">
                  <Smartphone className="h-12 w-12" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Controlled Environment Agriculture</h3>
                <p className="text-gray-600 mb-6">
                  Advanced greenhouses and vertical farms with automated climate control, hydroponics, and AI-optimized growth conditions for year-round production.
                </p>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.3}>
              <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                <div className="text-avn-purple-600 mb-6">
                  <Leaf className="h-12 w-12" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Regenerative Systems</h3>
                <p className="text-gray-600 mb-6">
                  Technology-enhanced regenerative farming techniques that restore soil health, increase biodiversity, and capture carbon while producing food.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechSection;